/**
 * Curated open-source font catalog for the Brand Kit font picker.
 *
 * Every font here is a Google Fonts family published under SIL Open Font
 * License or Apache 2.0 — safe to embed on commercial websites at no cost.
 * Categorization + vibe tags drive the filter UI. Pair suggestions let the
 * picker recommend a body font once the admin picks a heading.
 *
 * Industry recommendations drive the "no logo analysis available" fallback
 * during auto-recommendation (see §10 of the brand-kit plan).
 */

export type FontCategory =
  | 'serif'
  | 'sans-serif'
  | 'display'
  | 'monospace'
  | 'rounded'
  | 'humanist';
export type FontVibe =
  | 'modern'
  | 'classic'
  | 'playful'
  | 'luxurious'
  | 'technical'
  | 'friendly'
  | 'editorial';

export interface FontPreset {
  /** Google Fonts family name, used for both slug and display. */
  family: string;
  category: FontCategory;
  vibes: FontVibe[];
  /** Families from this list that pair well as body text. */
  pairsWith?: string[];
  /** Short description shown in the picker. */
  description: string;
}

export const FONT_PRESETS: FontPreset[] = [
  // ── Sans-serif ────────────────────────────────────────────────────────────
  {
    family: 'Inter',
    category: 'sans-serif',
    vibes: ['modern', 'technical', 'friendly'],
    description: 'Workhorse UI sans, crisp at every size.',
  },
  {
    family: 'Manrope',
    category: 'sans-serif',
    vibes: ['modern', 'friendly'],
    pairsWith: ['Inter', 'Manrope'],
    description: 'Rounded modern geometric sans.',
  },
  {
    family: 'DM Sans',
    category: 'sans-serif',
    vibes: ['modern', 'friendly'],
    description: 'Warm neo-grotesque with personality.',
  },
  {
    family: 'Plus Jakarta Sans',
    category: 'sans-serif',
    vibes: ['modern', 'friendly'],
    description: 'Clean, slightly geometric — great for SaaS.',
  },
  {
    family: 'Space Grotesk',
    category: 'sans-serif',
    vibes: ['technical', 'modern'],
    pairsWith: ['Inter', 'DM Sans'],
    description: 'Tech-forward, slightly quirky letterforms.',
  },
  {
    family: 'Work Sans',
    category: 'sans-serif',
    vibes: ['modern', 'friendly'],
    description: 'Open aperture, balanced for UI and body.',
  },
  {
    family: 'Nunito Sans',
    category: 'sans-serif',
    vibes: ['friendly', 'modern'],
    description: 'Soft rounded terminals, friendly and legible.',
  },
  {
    family: 'IBM Plex Sans',
    category: 'sans-serif',
    vibes: ['technical', 'modern'],
    pairsWith: ['IBM Plex Mono', 'IBM Plex Serif'],
    description: 'Corporate confidence, subtle engineered feel.',
  },
  {
    family: 'Sora',
    category: 'sans-serif',
    vibes: ['modern', 'technical'],
    description: 'Geometric, architectural.',
  },
  {
    family: 'Outfit',
    category: 'sans-serif',
    vibes: ['modern', 'luxurious'],
    description: 'Sophisticated modern sans with display weights.',
  },

  // ── Serif ─────────────────────────────────────────────────────────────────
  {
    family: 'Playfair Display',
    category: 'serif',
    vibes: ['luxurious', 'editorial', 'classic'],
    pairsWith: ['Inter', 'Work Sans', 'DM Sans'],
    description: 'High-contrast editorial serif with dramatic thicks and thins.',
  },
  {
    family: 'Fraunces',
    category: 'serif',
    vibes: ['playful', 'editorial'],
    pairsWith: ['Inter', 'DM Sans', 'Manrope'],
    description: 'Expressive variable serif; playful wedge terminals.',
  },
  {
    family: 'Libre Baskerville',
    category: 'serif',
    vibes: ['classic', 'editorial'],
    pairsWith: ['Inter', 'Work Sans'],
    description: 'Modern take on classic Baskerville — great for long-form reading.',
  },
  {
    family: 'Cormorant Garamond',
    category: 'serif',
    vibes: ['luxurious', 'editorial'],
    pairsWith: ['Inter', 'Work Sans', 'DM Sans'],
    description: 'Elegant, high-contrast Garamond revival.',
  },
  {
    family: 'Lora',
    category: 'serif',
    vibes: ['editorial', 'classic'],
    pairsWith: ['Inter', 'Manrope'],
    description: 'Balanced contemporary serif, good for body text.',
  },
  {
    family: 'EB Garamond',
    category: 'serif',
    vibes: ['classic', 'editorial'],
    description: 'Formal, book-grade Garamond.',
  },
  {
    family: 'Source Serif Pro',
    category: 'serif',
    vibes: ['classic', 'technical'],
    description: "Adobe's utility serif, screen-optimized.",
  },

  // ── Display ──────────────────────────────────────────────────────────────
  {
    family: 'Bricolage Grotesque',
    category: 'display',
    vibes: ['modern', 'playful'],
    pairsWith: ['Inter', 'DM Sans'],
    description: 'Variable display sans, condensed to expanded.',
  },
  {
    family: 'Archivo',
    category: 'display',
    vibes: ['modern', 'technical'],
    description: 'Grotesque with broad weight range, good for headlines.',
  },
  {
    family: 'Lexend',
    category: 'display',
    vibes: ['modern', 'friendly'],
    description: 'Research-driven, optimised for reading proficiency.',
  },
  {
    family: 'Unbounded',
    category: 'display',
    vibes: ['modern', 'playful'],
    pairsWith: ['Inter', 'Manrope'],
    description: 'Generous, confident display sans.',
  },

  // ── Rounded ──────────────────────────────────────────────────────────────
  {
    family: 'Quicksand',
    category: 'rounded',
    vibes: ['friendly', 'playful'],
    description: 'Soft rounded geometric — great for consumer brands.',
  },
  {
    family: 'Varela Round',
    category: 'rounded',
    vibes: ['friendly', 'playful'],
    description: 'Friendly, approachable, very round.',
  },
  {
    family: 'Nunito',
    category: 'rounded',
    vibes: ['friendly', 'modern'],
    description: 'Balanced rounded sans with full weight range.',
  },

  // ── Humanist ─────────────────────────────────────────────────────────────
  {
    family: 'PT Sans',
    category: 'humanist',
    vibes: ['classic', 'friendly'],
    description: 'Humanist classic, very readable at small sizes.',
  },
  {
    family: 'Roboto',
    category: 'humanist',
    vibes: ['modern', 'technical'],
    description: "Neo-grotesque with humanist touches. Google's default.",
  },
  {
    family: 'Open Sans',
    category: 'humanist',
    vibes: ['modern', 'friendly'],
    description: 'Friendly web workhorse.',
  },

  // ── Monospace ────────────────────────────────────────────────────────────
  {
    family: 'JetBrains Mono',
    category: 'monospace',
    vibes: ['technical', 'modern'],
    description: 'Developer-focused mono with generous spacing.',
  },
  {
    family: 'Fira Code',
    category: 'monospace',
    vibes: ['technical', 'modern'],
    description: 'Programming ligatures, distinctive letterforms.',
  },
  {
    family: 'IBM Plex Mono',
    category: 'monospace',
    vibes: ['technical'],
    pairsWith: ['IBM Plex Sans', 'IBM Plex Serif'],
    description: 'Corporate-code aesthetic — pairs with Plex Sans.',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Industry → heading/body recommendation
// ─────────────────────────────────────────────────────────────────────────────

export interface FontRecommendation {
  heading: string;
  body: string;
  reason: string;
}

const INDUSTRY_RECOMMENDATIONS: Record<string, FontRecommendation> = {
  fashion: {
    heading: 'Playfair Display',
    body: 'Inter',
    reason: 'Editorial serif with clean body pairing — evokes premium fashion publications.',
  },
  'tech-saas': {
    heading: 'Space Grotesk',
    body: 'Inter',
    reason: 'Tech-forward display sans with trusted UI body.',
  },
  food: {
    heading: 'Fraunces',
    body: 'DM Sans',
    reason: 'Warm, playful serif with rounded body — appetising and approachable.',
  },
  ecommerce: {
    heading: 'Outfit',
    body: 'Inter',
    reason: 'Modern luxury with reliable body legibility.',
  },
  beauty: {
    heading: 'Cormorant Garamond',
    body: 'Work Sans',
    reason: 'High-end serif elegance with clean sans body.',
  },
  wellness: {
    heading: 'Fraunces',
    body: 'Nunito Sans',
    reason: 'Soft warmth suitable for mindful, human brands.',
  },
  education: {
    heading: 'Lora',
    body: 'Open Sans',
    reason: 'Academic, trustworthy serif with universally readable body.',
  },
  finance: {
    heading: 'Source Serif Pro',
    body: 'IBM Plex Sans',
    reason: 'Professional and precise — signals reliability.',
  },
  legal: {
    heading: 'Libre Baskerville',
    body: 'PT Sans',
    reason: 'Formal serif authority with dense, readable body.',
  },
  default: {
    heading: 'Inter',
    body: 'Inter',
    reason: 'Safe, modern default — works for virtually any brand.',
  },
};

/**
 * Recommend a font pair based on industry (if available).
 * Falls back to "default" pair if industry is not recognised.
 */
export function recommendFontPair(industry?: string): FontRecommendation {
  if (!industry) return INDUSTRY_RECOMMENDATIONS.default;
  const key = industry.toLowerCase().replace(/\s+/g, '-');
  return INDUSTRY_RECOMMENDATIONS[key] ?? INDUSTRY_RECOMMENDATIONS.default;
}

// ─────────────────────────────────────────────────────────────────────────────
// Trending pairs fallback (used when neither logo-font detection nor industry
// recommendation is available).
// ─────────────────────────────────────────────────────────────────────────────

export const TRENDING_PAIRS: FontRecommendation[] = [
  {
    heading: 'Bricolage Grotesque',
    body: 'Inter',
    reason: 'Trending: editorial + minimal body.',
  },
  {
    heading: 'Fraunces',
    body: 'DM Sans',
    reason: 'Trending: playful serif + rounded body.',
  },
  {
    heading: 'Outfit',
    body: 'Plus Jakarta Sans',
    reason: 'Trending: luxury display + clean body.',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Google Fonts URL builder
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a Google Fonts CSS URL for a family with standard weights.
 * Example: `https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap`
 */
export function buildGoogleFontsUrl(
  families: Array<{ family: string; weights?: number[] }>
): string {
  const parts = families.map(({ family, weights = [400, 500, 600, 700] }) => {
    const name = family.replace(/\s+/g, '+');
    const w = weights.join(';');
    return `family=${name}:wght@${w}`;
  });
  return `https://fonts.googleapis.com/css2?${parts.join('&')}&display=swap`;
}
